import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {ChatSessionQuery, ChatSessionQueryVariables} from 'llm/chat-library/__generated/ChatSessionQuery';
import {CHAT_SESSION_QUERY} from 'llm/chat-library/useChatSessionEvents';

/**
 * Resolve the owning agent (routine) id for a chat session.
 *
 * The tool-call detail modal mounts outside the chat's React context (via the global legacy modal
 * container), so the routine id cannot be read from context — it is resolved from the chat session
 * through Apollo instead. Uses `cache-first` because the session query is already loaded for any open
 * chat, so this is a cache hit in the common case.
 *
 * Returns null while loading, on error, or when the session has no accessible owning routine.
 */
export function useChatSessionRoutineId(chatSessionId: string | null | undefined): string | null {
  const result = useSkippableBenchlingQuery<ChatSessionQuery, ChatSessionQueryVariables>(CHAT_SESSION_QUERY, {
    variables: {chatSessionId: chatSessionId ?? '', disableAllThreads: true},
    skip: chatSessionId == null,
    fetchPolicy: 'cache-first',
  });
  if (result.type !== QueryResultType.SUCCESS) {
    return null;
  }
  const {chatSession} = result.data;
  if (chatSession.__typename !== 'ChatSessionNode') {
    return null;
  }
  const {routineExecution} = chatSession;
  if (routineExecution?.__typename !== 'RoutineExecutionNode') {
    return null;
  }
  return routineExecution.routine.id;
}
